// src/pages/AdminDashboard.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { ref, get } from 'firebase/database';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTeachers: 0,
    totalClasses: 0,
    totalSubjects: 0,
    totalPeriods: 0,
    monthlyArrangements: 0,
    presentTeachers: 0,
    absentTeachers: 0,
    onDutyTeachers: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    // Check admin authentication
    const isAdminLoggedIn = sessionStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/login');
      return;
    }

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      // Fetch all data in parallel
      const [teachersSnap, classesSnap, subjectsSnap, timetablesSnap, arrangementsSnap, teacherStatusSnap] = await Promise.all([
        get(ref(db, 'teachers')),
        get(ref(db, 'classes')),
        get(ref(db, 'subjects')),
        get(ref(db, 'timetables')),
        get(ref(db, 'arrangementHistory')),
        get(ref(db, `teacherStatus/${today}`))
      ]);

      // Calculate total teachers
      let totalTeachers = 0;
      if (teachersSnap.exists()) {
        const teachersData = teachersSnap.val();
        const activeTeachers = Object.values(teachersData).filter(t => t.status !== 'inactive');
        totalTeachers = activeTeachers.length;
      }

      // Calculate total classes
      let totalClasses = 0;
      if (classesSnap.exists()) {
        const classesData = classesSnap.val();
        totalClasses = Object.keys(classesData).length;
      }

      // Calculate total subjects
      let totalSubjects = 0;
      if (subjectsSnap.exists()) {
        const subjectsData = subjectsSnap.val();
        totalSubjects = Object.keys(subjectsData).length;
      }

      // Calculate total weekly periods
      let totalPeriods = 0;
      if (timetablesSnap.exists()) {
        const timetablesData = timetablesSnap.val();
        Object.values(timetablesData).forEach(classData => {
          if (classData && typeof classData === 'object') {
            Object.values(classData).forEach(dayData => {
              if (dayData && typeof dayData === 'object') {
                totalPeriods += Object.keys(dayData).length;
              }
            });
          }
        });
      }

      // Calculate monthly arrangements
      let monthlyArrangements = 0;
      if (arrangementsSnap.exists()) {
        const arrangements = arrangementsSnap.val();
        Object.values(arrangements).forEach(arr => {
          if (arr && arr.date) {
            const date = new Date(arr.date);
            if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
              monthlyArrangements++;
            }
          }
        });
      }

      // Calculate teacher attendance for today
      let presentTeachers = 0, absentTeachers = 0, onDutyTeachers = 0;
      if (teacherStatusSnap.exists()) {
        const statusData = teacherStatusSnap.val();
        Object.values(statusData).forEach(status => {
          if (status.status === 'Present') presentTeachers++;
          else if (status.status === 'Absent') absentTeachers++;
          else if (status.status === 'On Duty') onDutyTeachers++;
        });
      }

      setStats({
        totalTeachers,
        totalClasses,
        totalSubjects,
        totalPeriods,
        monthlyArrangements,
        presentTeachers,
        absentTeachers,
        onDutyTeachers
      });

      // Get recent activities (last 5 arrangements)
      if (arrangementsSnap.exists()) {
        const arrangements = arrangementsSnap.val();
        const sortedArrangements = Object.values(arrangements)
          .filter(arr => arr && arr.date)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 5);
        setRecentActivities(sortedArrangements);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    navigate('/login');
  };

  const quickActions = [
    { title: 'Teacher Timetable', icon: '👨‍🏫', color: '#4CAF50', path: '/admin/teacher-timetable' },
    { title: 'Class Timetable', icon: '📚', color: '#2196F3', path: '/admin/class-timetable' },
    { title: 'Teacher Workload', icon: '📈', color: '#FF9800', path: '/admin/teacher-workload' },
    { title: 'Teacher Report', icon: '📄', color: '#9C27B0', path: '/admin/teacher-report' }
  ];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.headerTitle}>🏫 MIS International School</h1>
          <span style={styles.headerSubtitle}>Admin Dashboard</span>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Stats Cards */}
        <div style={styles.statsGrid}>
          <div style={{...styles.statCard, borderLeftColor: '#2563eb'}}>
            <div style={styles.statIcon}>👨‍🏫</div>
            <div style={styles.statInfo}>
              <span style={styles.statNumber}>{stats.totalTeachers}</span>
              <span style={styles.statLabel}>Total Teachers</span>
            </div>
          </div>

          <div style={{...styles.statCard, borderLeftColor: '#16a34a'}}>
            <div style={styles.statIcon}>📚</div>
            <div style={styles.statInfo}>
              <span style={styles.statNumber}>{stats.totalClasses}</span>
              <span style={styles.statLabel}>Total Classes</span>
            </div>
          </div>

          <div style={{...styles.statCard, borderLeftColor: '#f59e0b'}}>
            <div style={styles.statIcon}>📖</div>
            <div style={styles.statInfo}>
              <span style={styles.statNumber}>{stats.totalSubjects}</span>
              <span style={styles.statLabel}>Total Subjects</span>
            </div>
          </div>

          <div style={{...styles.statCard, borderLeftColor: '#8b5cf6'}}>
            <div style={styles.statIcon}>⏰</div>
            <div style={styles.statInfo}>
              <span style={styles.statNumber}>{stats.totalPeriods}</span>
              <span style={styles.statLabel}>Weekly Periods</span>
            </div>
          </div>

          <div style={{...styles.statCard, borderLeftColor: '#dc2626'}}>
            <div style={styles.statIcon}>📋</div>
            <div style={styles.statInfo}>
              <span style={styles.statNumber}>{stats.monthlyArrangements}</span>
              <span style={styles.statLabel}>Arrangements This Month</span>
            </div>
          </div>
        </div>

        {/* Teacher Status Cards */}
        <div style={styles.statusGrid}>
          <div style={{...styles.statusCard, borderLeftColor: '#22c55e'}}>
            <h4 style={styles.statusTitle}>✅ Present Teachers</h4>
            <h1 style={{...styles.statusNumber, color: '#22c55e'}}>{stats.presentTeachers}</h1>
          </div>
          <div style={{...styles.statusCard, borderLeftColor: '#ef4444'}}>
            <h4 style={styles.statusTitle}>❌ Absent Teachers</h4>
            <h1 style={{...styles.statusNumber, color: '#ef4444'}}>{stats.absentTeachers}</h1>
          </div>
          <div style={{...styles.statusCard, borderLeftColor: '#f59e0b'}}>
            <h4 style={styles.statusTitle}>📋 On Duty Teachers</h4>
            <h1 style={{...styles.statusNumber, color: '#f59e0b'}}>{stats.onDutyTeachers}</h1>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.quickActionsSection}>
          <h2 style={styles.sectionTitle}>Quick Actions</h2>
          <div style={styles.quickActionsGrid}>
            {quickActions.map((action, index) => (
              <button
                key={index}
                style={{...styles.quickActionBtn, borderBottom: `4px solid ${action.color}`}}
                onClick={() => navigate(action.path)}
              >
                <span style={styles.actionIcon}>{action.icon}</span>
                <span style={styles.actionTitle}>{action.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={styles.recentActivitySection}>
          <h2 style={styles.sectionTitle}>Recent Activity</h2>
          <div style={styles.activityList}>
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} style={styles.activityItem}>
                  <span style={styles.activityIcon}>📅</span>
                  <div style={styles.activityContent}>
                    <div style={styles.activityTitle}>
                      {activity.class} - {activity.subject}
                    </div>
                    <div style={styles.activityDetails}>
                      <span>{activity.date}</span>
                      <span>•</span>
                      <span>{activity.period}</span>
                      <span>•</span>
                      <span>Sub: {activity.substituteTeacher}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.noActivity}>No recent arrangements found</div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>Powered by Jauhari WebTech</p>
        <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 100%)',
    color: 'white',
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerTitle: {
    fontSize: '18px',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '12px',
    opacity: 0.8,
    marginTop: '2px',
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: '15px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'white',
    padding: '15px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderLeft: '4px solid #2563eb',
  },
  statIcon: {
    fontSize: '28px',
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  statNumber: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#2c3e50',
  },
  statLabel: {
    fontSize: '11px',
    color: '#7f8c8d',
  },

  // Status Grid
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  },
  statusCard: {
    background: 'white',
    padding: '15px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderLeft: '4px solid #22c55e',
    textAlign: 'center',
  },
  statusTitle: {
    color: '#666',
    margin: '0 0 8px 0',
    fontSize: '13px',
  },
  statusNumber: {
    fontSize: '28px',
    fontWeight: 700,
    margin: 0,
  },

  // Quick Actions
  quickActionsSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#2c3e50',
    margin: '0 0 15px 0',
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },
  quickActionBtn: {
    background: '#f8f9fa',
    border: 'none',
    borderRadius: '12px',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '13px',
    color: '#2c3e50',
    borderBottom: '4px solid #4CAF50',
  },
  actionIcon: {
    fontSize: '28px',
  },
  actionTitle: {
    fontWeight: 500,
  },

  // Recent Activity
  recentActivitySection: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#f8f9fa',
    borderRadius: '8px',
  },
  activityIcon: {
    fontSize: '20px',
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '14px',
  },
  activityDetails: {
    display: 'flex',
    gap: '8px',
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '4px',
  },
  noActivity: {
    textAlign: 'center',
    color: '#7f8c8d',
    padding: '20px',
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f5f7fa',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #2d1b4e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Footer
  footer: {
    background: 'white',
    textAlign: 'center',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '20px',
  },
};

// Add keyframe animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default AdminDashboard;